import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import Icon from 'components/Icon';
import Loading from 'components/Loading';
import { usePrintManager } from 'hooks/usePrintManager';
import { AppContext } from 'shared/AppContext';
import { useCart } from 'shared/CartContext';
import { useGetAccount } from '../Accounts/gql/queries/useGetAccount';
import { useGetEvent } from '../Events/EventsMenu/gql/queries/get-event';
import { CartList } from './Components/CartList';
import { EmptyCart } from './Components/EmptyCart';
import { PaymentOption } from './Components/PaymentOption';
import { ReaderIpModal } from './Components/ReaderIpModal';
import { TicketHeader } from './Components/TicketHeader';
import { TicketList } from './Components/TicketList';

const MainContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  background-color: rgb(0, 0, 31);
  color: white;
  font-size: large;
  font-family: 'Barlow Condensed';
  height: 100dvh; /* Fixed height on larger screens */

  @media (max-width: 860px) {
    height: auto; /* Allows expansion based on content */
    min-height: 90vh; /* Ensures it starts from the viewport height */
    overflow-y: auto; /* Enables scrolling if content exceeds max-height */
  }
`;

const LeftContainer = styled.div`
  flex: 0 0 60%;
  padding: 20px;
  box-sizing: border-box;
  min-width: 60%;
  height: 100dvh;

  @media (max-width: 860px) {
    min-width: 100%;
    overflow: auto;
    height: 60%;
  }
`;

const RightContainer = styled.div`
  flex: 0 0 40%;
  padding: 20px 20px 20px 5px;
  box-sizing: border-box;
  height: 100%;
  display: flex;
  min-width: 40%;

  @media (max-width: 860px) {
    min-width: 100%;
    padding-left: 20px;
    max-height: 100vh;
  }
`;

export const FloatingCartButton = styled.button`
  position: fixed;
  bottom: 15px;
  right: 15px;
  background-color: #fa4616;
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  font-size: 18px;
  font-weight: bold;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  cursor: pointer;

  @media (min-width: 860px) {
    display: none;
  }
`;

export const CountBadge = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
  background-color: white;
  color: #fa4616;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-size: 14px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CartPage = () => {
  const params = useParams();
  const EventId = parseInt(params.id);

  const {
    ticketCount,
    setSeatId,
    setAccountId,
    accountId,
    event,
    setEvent,
    setAccount,
    tickets,
    setSelected,
  } = useCart();

  const { data, loading } = useGetEvent(EventId);

  const {
    state: { account },
  } = useContext(AppContext);

  const { data: accountData } = useGetAccount(
    event?.account_id,
    !event || account
  );

  const [isCheckoutVisible, setIsCheckoutVisible] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const payment_intent = urlParams.get('payment_intent');
  const payment_id = urlParams.get('payment_intent_client_secret');

  const { isConnected } = usePrintManager();
  const [isReaderIpVisible, setIsReaderIpVisible] = useState(false);

  useEffect(() => {
    // Clear storage initially
    localStorage.removeItem('tickets');
    localStorage.removeItem('selectedTicket');

    // Remove any old timer keys
    Object.keys(localStorage).forEach((key) => {
      if (key.includes('timer')) localStorage.removeItem(key);
    });
  }, []);

  useEffect(() => {
    if (data && data.getEvent) {
      setEvent(data.getEvent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (accountData && accountData.getAccount) {
      setAccount(accountData.getAccount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountData]);

  useEffect(() => {
    if (event && event.seats_id) {
      setSeatId(event.seats_id);
    }

    if (event && event.account_id) {
      setAccountId(event.account_id);
    }
  }, [data, event, setSeatId, setAccountId]);

  useEffect(() => {
    // Check if payment_intent and payment_id are in the url and open payment option modal if they are
    if (payment_intent && payment_id && accountId) {
      setIsCheckoutVisible(true);
    }
  }, [payment_intent, payment_id, accountId]);

  const connectionStatus = useMemo(() => {
    return isConnected();
  }, [isConnected]);

  useEffect(() => {
    const isPrintRequired =
      event?.pos_settings?.ticket_delivery === 'print' ||
      event?.pos_settings?.receipt_delivery === 'print';

    if (isPrintRequired && !connectionStatus) {
      setIsReaderIpVisible(true);
    } else {
      setIsReaderIpVisible(false);
    }
  }, [connectionStatus, event]);

  // Reset selected ticket on initial load
  useEffect(() => {
    setSelected(Object.keys(tickets)[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cartRef = React.useRef(null);

  const scrollToCart = () => {
    // eslint-disable-next-line no-unused-expressions
    cartRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading && !data) {
    return <Loading />;
  }

  return (
    <MainContainer>
      <LeftContainer>
        <TicketHeader event={event} />
        <TicketList />
      </LeftContainer>
      <RightContainer>
        {ticketCount === 0 ? <EmptyCart event={event} /> : <CartList />}
      </RightContainer>

      {ticketCount > 0 && (
        <CartFloatingButton count={ticketCount} onClick={scrollToCart} />
      )}

      <PaymentOption
        isVisible={isCheckoutVisible}
        setIsVisible={setIsCheckoutVisible}
        manualCardPayment={!!payment_intent}
        accountIdPayment={accountId}
      />

      <ReaderIpModal
        isVisible={isReaderIpVisible}
        setIsVisible={setIsReaderIpVisible}
      />
    </MainContainer>
  );
};

const CartFloatingButton = ({ count, onClick }) => {
  if (count === 0) return null;

  return (
    <FloatingCartButton onClick={onClick}>
      <Icon icon="shopping_cart" size="28" />
      <CountBadge>{count}</CountBadge>
    </FloatingCartButton>
  );
};

export default CartPage;
